import isString from 'lodash/isString';

export type QueryDataObjectType = {
  street: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  unit: string | null;
} & FilterParamsType;

export type FilterParamsType = {
  activeTypes?: string | null;
  amenities?: string | null;
  bedrooms?: string | null;
  activatedDays?: number | null;
  deactivatedDays?: number | null;
  distanceInMiles?: number | null;
  laundry?: string | null;
  limit?: number | null;
  offset?: number | null;
  orderBy?: string;
  pets?: string | null;
  price?: number;
  propertyTypes?: string | null;
};

const getFilterString = (filterName: string, filterValue: string | number | null) => {
  if (filterValue) {
    if (isString(filterValue)) {
      return `${filterName}: "${filterValue}",`;
    } else {
      return `${filterName}: ${filterValue},`;
    }
  }
  return '';
};

const geoFields = `
        geo {
            lat
            lon
        }
`;

const addressDetailsFields = `
        floorplans {
            zpid
            numBeds
            numFullBaths
            zestimate {
                rentZestimate
                rentZestimateRangeLow
                rentZestimateRangeHigh
            }
            minSqft
            maxSqft
            address {
                street
                city
                state
                zip
                unit
            }
        }
`;

const marketSummaryFields = `
        marketSummary {
            areaName
            beds
            summary {
                medianRent
                monthlyChange
                yearlyChange
                avgDaysOnMarket
                availableRentals
            }
        }
`;

const comparablesFields = (filterParams: FilterParamsType) => {
  const {
    activeTypes = null,
    activatedDays = null,
    amenities = null,
    bedrooms = null,
    deactivatedDays = null,
    distanceInMiles = null,
    laundry = null,
    limit = null,
    offset = null,
    orderBy = 'lowPrice',
    pets = null,
    price = 0,
    propertyTypes = null,
  } = filterParams;
  return `
        similarFloorplans (
            filter: {
                ${getFilterString('activeTypes', activeTypes)}
                ${getFilterString('activatedDays', activatedDays)}
                ${getFilterString('deactivatedDays', deactivatedDays)}
                ${getFilterString('propertyTypes', propertyTypes)}
                ${getFilterString('bedrooms', bedrooms)}
                ${getFilterString('pets', pets)}
                ${getFilterString('laundry', laundry)}
                ${getFilterString('amenities', amenities)}
                ${getFilterString('orderBy', orderBy)}
                ${getFilterString('distanceInMiles', distanceInMiles)}
                ${getFilterString('price', price)}
                ${getFilterString('limit', limit)}
                ${getFilterString('offset', offset)}
            }
        ) {
            listingRanking
            highestPriceIncluded
            lowestPriceIncluded
            boundingBox{
                minLat
                maxLat
                minLon
                maxLon
            }
            floorplans{
                aliasEncoded
                floorplanGroupId
                active
                zpid
                numBeds
                numFullBaths
                numPartialBaths
                numUnitsAvailable
                propertyUrl
                lowPrice
                highPrice
                distanceInMiles
                minSqft
                maxSqft
                address{
                    street
                }
                geo{
                    lat
                    lon
                    quad
                }
                amenities{
                    laundry{
                        inUnit
                        shared
                    }
                    parking{
                        valet
                        garage
                        offStreet
                    }
                    hvac{
                        airConditioning
                        heating
                    }
                    pets{
                        cats
                        largeDogs
                        smallDogs
                    }
                }
                history{
                    lastUpdated{
                        date
                        ago
                    }
                    deactivated{
                        date
                        ago
                    }
                }
                photos{
                    listingPhotos{
                        medium
                    }
                }
            }
        }
    `;
};

export const getFullQuery = (params: QueryDataObjectType) => {
  const { street, city, state, zip, unit, ...filterParams } = params;

  return `{
        byAddress(
            street: "${street}"
            city: "${city}"
            state: "${state}"
            zip: "${zip}"
            unit: "${unit}"
        ) {
            ${geoFields}
            ${addressDetailsFields}
            ${marketSummaryFields}
            ${comparablesFields(filterParams)}
        }
    }`.replace(/(^[ \t]*\n)/gm, ''); // finally remove empty lines
};

export const getCompsOnlyQuery = (params: QueryDataObjectType) => {
  const { street, city, state, zip, unit, ...filterParams } = params;

  return `{
        byAddress(
            street: "${street}"
            city: "${city}"
            state: "${state}"
            zip: "${zip}"
            unit: "${unit}"
        ) {
            ${geoFields}
            ${comparablesFields(filterParams)}
        }
    }`.replace(/(^[ \t]*\n)/gm, ''); // finally remove empty lines
};
